import { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { FiltersContext } from "components/Filters";
import { ArrowDown, ArrowUp } from "components/icons/generated";
import { URL_SORT_DIRECTION, URL_SORT_KEY } from "constants/urlQueryKeys";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownWrapper from "ds/components/Dropdown/DropdownWrapper";
import DropdownFooterSwitch from "ds/components/Dropdown/Footer/Switch";
import DropdownList from "ds/components/Dropdown/List";
import DropdownSection from "ds/components/Dropdown/Section";
import DropdownSectionItem from "ds/components/Dropdown/SectionItem";
import Icon from "ds/components/Icon";
import Radio from "ds/components/Radio";
import Typography from "ds/components/Typography";
import useEscapeKeypress from "hooks/useEscapeKeyPress";
import useOutsideClick from "hooks/useOutsideClick";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryOrderDirection } from "types/generated";
import { decodeURIParam } from "utils/urls";

import { getSessionStorageKeys } from "../helpers";
import styles from "./styles.module.css";

const SortDropdown = () => {
  const [isVisible, setIsVisible] = useState(false);
  const wrapperRef = useRef(null);

  const storage = sessionStorage;

  const [searchParams, setSearchParams] = useSearchParams();
  const { sortOptions, initialSortOption, initialSortDirection, filtersOrderSettingsKey } =
    useTypedContext(FiltersContext);
  const { storageUrlSortDirection, storageUrlSortKey } =
    getSessionStorageKeys(filtersOrderSettingsKey);

  const [querySortOption, querySortDirection] = useMemo(() => {
    const querySortOption = decodeURIParam(searchParams.get(URL_SORT_KEY)) || initialSortOption;
    const querySortDirection = (decodeURIParam(searchParams.get(URL_SORT_DIRECTION)) ||
      initialSortDirection) as SearchQueryOrderDirection;

    return [querySortOption, querySortDirection];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSortOptionChange = useCallback(
    (option: string | null, direction: SearchQueryOrderDirection | null, clb?: () => void) =>
      () => {
        if (option === null) {
          searchParams.delete(URL_SORT_KEY);
          storage.removeItem(storageUrlSortKey);
        } else {
          searchParams.set(URL_SORT_KEY, encodeURIComponent(option));
          storage.setItem(storageUrlSortKey, encodeURIComponent(option));
        }

        if (direction === null) {
          searchParams.delete(URL_SORT_DIRECTION);
          storage.removeItem(storageUrlSortDirection);
        } else {
          searchParams.set(URL_SORT_DIRECTION, encodeURIComponent(direction));
          storage.setItem(storageUrlSortDirection, encodeURIComponent(direction));
        }

        setSearchParams(searchParams);

        clb?.();
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  );

  const handleClick = () => {
    setIsVisible(!isVisible);
  };

  const closeDropdown = () => {
    setIsVisible(false);
  };

  const currentSortOption = useMemo(() => {
    return sortOptions.find(({ value }) => value === querySortOption);
  }, [querySortOption, sortOptions]);

  useOutsideClick(wrapperRef, closeDropdown);

  useEscapeKeypress(isVisible, closeDropdown);

  return (
    <DropdownWrapper ref={wrapperRef}>
      {currentSortOption?.label && (
        <Box gap="medium" align="center">
          <Typography tag="span" variant="p-t7" color="secondary" aria-hidden>
            Sort by:
          </Typography>
          <Button
            variant="secondary"
            onPress={handleClick}
            active={isVisible}
            size="small"
            endIcon={ArrowUp}
            endIconRotate={
              querySortDirection === SearchQueryOrderDirection.Desc ? "180" : undefined
            }
            aria-label={`Sort by: ${currentSortOption.label}${querySortDirection === SearchQueryOrderDirection.Desc ? ", Descending" : "Ascending"}`}
          >
            <span className={styles.buttonText}>{currentSortOption.label}</span>
          </Button>
        </Box>
      )}

      <DropdownList active={isVisible} position="bottomLeft">
        <DropdownSection>
          {sortOptions &&
            sortOptions.length > 0 &&
            sortOptions.map((option) => (
              <DropdownSectionItem
                key={option.value}
                onClick={handleSortOptionChange(option.value, querySortDirection)}
                className={styles.sortDropdownItem}
              >
                <Radio
                  key={option.value}
                  id={option.value}
                  checked={option.value === querySortOption}
                >
                  {option.label}
                </Radio>
              </DropdownSectionItem>
            ))}
        </DropdownSection>

        <DropdownFooterSwitch
          onChange={(value) => handleSortOptionChange(querySortOption, value)()}
          value={querySortDirection}
          options={[
            {
              label: (
                <Box gap="small" align="center" padding="0 medium">
                  <Icon src={ArrowUp} />
                  Ascending
                </Box>
              ),
              value: SearchQueryOrderDirection.Asc,
            },
            {
              label: (
                <Box gap="small" align="center" padding="0 medium">
                  <Icon src={ArrowDown} />
                  Descending
                </Box>
              ),
              value: SearchQueryOrderDirection.Desc,
            },
          ]}
        />
      </DropdownList>
    </DropdownWrapper>
  );
};

export default SortDropdown;
